import {
	Column,
	CreateDateColumn,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	UpdateDateColumn,
} from 'typeorm';
import { BaseEntity } from './base.entity';
import { ConversationAgentEntity } from './conversation-agent.entity';

/**
 * ConversationPhoneEntity - Maps phone numbers to AI agents within a tenant
 * This is stored in each tenant's database to enable agent assignment
 *
 * Flow:
 * 1. Inbound call to phone number
 * 2. Login API lookup: phone → instanceId
 * 3. CRM-API sets tenant context
 * 4. CRM-API lookup: phone → agentId (this table)
 * 5. Start conversation with assigned agent
 */
@Entity({ name: '_conversation_phone' })
export class ConversationPhoneEntity extends BaseEntity {
	/**
	 * Phone number in E.164 format (e.g., +12484796564)
	 * A phone can only be assigned to one agent at a time within a tenant
	 */
	@Column({ type: 'varchar', length: 20 })
	@Index()
	public phoneNumber!: string;

	/**
	 * The AI agent assigned to handle calls/messages to this phone number
	 */
	@Column({ type: 'int', unsigned: true })
	@Index()
	public agentId!: number;

	@ManyToOne(() => ConversationAgentEntity)
	@JoinColumn({ name: 'agentId' })
	public agent?: ConversationAgentEntity;

	/**
	 * Whether this assignment is currently active
	 * Inactive assignments are kept for historical records
	 */
	@Column({ type: 'boolean', default: true })
	@Index()
	public isActive!: boolean;

	/**
	 * The Twilio subaccount (`AC...`) the agent's number is provisioned on,
	 * once the tenant is migrated to per-tenant subaccounts (PD-14218).
	 * NULL until cutover — the runtime resolves it via PhoneLookupService
	 * and falls back to the master account, so null = "still on master".
	 */
	@Column({ type: 'varchar', length: 64, nullable: true })
	public subAccountSid!: string | null;

	@CreateDateColumn()
	public createdAt!: Date;

	@UpdateDateColumn()
	public updatedAt!: Date;
}
